/**
 * Proof chips on the job page (trust audit Phase 2, slice 3; mockup
 * https://claude.ai/artifact/F74A1dCrf6HQVPiJBL9XHE, "2. Job page").
 *
 *   - Results: each leg's chip (a detector's main and bypass each get their
 *     own). A tech's own readings are a small gray ✓; a best guess or a
 *     missing result always says so in words.
 *   - The paper hand-off: a paper FAIL waiting on the tech reads "? Missing:
 *     Elijah's readings off the paper", and the fix asks him (a task the
 *     office writes; nothing is sent on its own).
 *   - "What the tech will do": each line says where it came from (see
 *     office-tech-plan.tsx), and a failed device with no line is ?.
 *
 * On for everyone; the per-person "How sure" switch (`trust.proof-chips`) is
 * an opt-out. Turned off, nothing is fetched and the page is exactly as before.
 * The server decides every label (GET /api/jobs/:id/proof,
 * services/jobProof.ts); this file only draws it. Staff screens only.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useProofChipsSwitch, type Proof, type ProofFix, type ProofPlace } from "@/lib/proof";
import { ProofChip } from "@/components/proof/proof-chip";
import { ProofChipsHelp, ProofChipsToggle } from "@/components/proof/proof-help";
import { TaskCreateDialog } from "@/components/task-create-dialog";

export interface LegProof {
    main: Proof | null;
    bypass: Proof | null;
}

export interface ScopeGap {
    assetId: string;
    leg: "main" | "bypass";
    label: string;
    proof: Proof;
}

export interface JobProof {
    jobId: string;
    tests: Record<string, LegProof>;
    scope: Record<string, Proof>;
    scopeGaps: ScopeGap[];
}

/** Under the job's own key, so every job save refreshes the chips too. */
export function jobProofKey(jobId: string | undefined) {
    return ["job", jobId, "proof"] as const;
}

export function useJobProof(jobId: string | undefined) {
    const sw = useProofChipsSwitch();
    const q = useQuery({
        queryKey: jobProofKey(jobId),
        queryFn: async () => (await api.get<{ proof: JobProof }>(`/api/jobs/${jobId}/proof`)).proof,
        enabled: !!jobId && sw.on,
        staleTime: 30_000,
        retry: false,
    });
    return {
        allowed: sw.allowed,
        on: sw.on,
        set: sw.set,
        // A failed read just means no chips: the page never depends on them.
        proof: sw.on && q.data ? q.data : null,
    };
}

interface JobProofCtx {
    proof: JobProof | null;
    /** The page's own Upload button ("upload_report"). */
    onUploadReport?: (() => void) | null;
}

const Ctx = createContext<JobProofCtx>({ proof: null });

/** The job page wraps its body in this; the rows below read their chips from it. */
export function JobProofProvider({ proof, onUploadReport, children }: JobProofCtx & { children: ReactNode }) {
    return <Ctx.Provider value={{ proof, onUploadReport }}>{children}</Ctx.Provider>;
}

export function useJobProofContext(): JobProofCtx {
    return useContext(Ctx);
}

/** "ⓘ What the chips mean · How sure: Off | On", by the job's devices. */
export function JobProofControls({ on, set }: { on: boolean; set: (on: boolean) => void }) {
    return (
        <div className="flex flex-wrap items-center justify-end gap-2" data-testid="job-proof-controls">
            {on && <ProofChipsHelp />}
            <ProofChipsToggle on={on} onChange={set} />
        </div>
    );
}

/** Which leg a device row reads: its own test's main, or its main's test's bypass. */
export function legProofFor(
    proof: JobProof | null,
    test: { id: string } | undefined,
    inheritedTest: { id: string } | undefined,
): Proof | null {
    if (!proof) return null;
    if (test) return proof.tests[test.id]?.main ?? null;
    if (inheritedTest) return proof.tests[inheritedTest.id]?.bypass ?? null;
    return null;
}

/**
 * One device row's chip. "Ask the tech" opens a task the office writes,
 * linked to the device and the job; "Upload the report" is the page's own
 * Upload button.
 */
export function LegProofChip({
    test,
    inheritedTest,
    asset,
    jobProof,
    onUploadReport: onUploadProp,
    place = "busy",
    className,
}: {
    test: { id: string } | undefined;
    inheritedTest?: { id: string } | undefined;
    asset: { asset_id: string; label?: string | null; serial_number?: string | null };
    /** The job's proof, handed down (falls back to a JobProofProvider). Null = switch off. */
    jobProof?: JobProof | null;
    /** The page's own Upload button (falls back to the provider's). */
    onUploadReport?: (() => void) | null;
    place?: ProofPlace;
    className?: string;
}) {
    const ctx = useJobProofContext();
    const proof = jobProof !== undefined ? jobProof : ctx.proof;
    const onUploadReport = onUploadProp !== undefined ? onUploadProp : ctx.onUploadReport;
    const [asking, setAsking] = useState(false);
    const p = legProofFor(proof, test, inheritedTest);
    if (!p) return null;
    const jobId = proof?.jobId;
    const label = asset.label || (asset.serial_number ? `SN ${asset.serial_number}` : "this device");
    const paper = /off the paper$/.test(p.words);
    const onFix = (fix: ProofFix) => {
        if (fix.action === "ask_tech") setAsking(true);
        else if (fix.action === "upload_report") onUploadReport?.();
    };
    return (
        <>
            <ProofChip proof={p} place={place} onFix={onFix} testId="leg-proof-chip" className={className} />
            {asking && (
                <TaskCreateDialog
                    open={asking}
                    onOpenChange={setAsking}
                    prefill={{
                        title: paper
                            ? `Please type the readings off the signed paper: ${label}`
                            : `Please enter the bypass result: ${label}`,
                        links: [
                            { entityType: "asset", entityId: asset.asset_id, label },
                            ...(jobId ? [{ entityType: "job", entityId: jobId, label: "Job" }] : []),
                        ],
                    }}
                />
            )}
        </>
    );
}
